<template>
  <div class="candidate-page">
    <t-card :bordered="false" class="panel">
      <template #title>D加密票据候选</template>
      <div class="toolbar">
        <t-input v-model="search" clearable placeholder="搜索 SteamID / 游戏星球账号 / AppID / 游戏名" class="search" @enter="loadData(1)" />
        <t-button theme="primary" :loading="loading" @click="loadData(1)">查询</t-button>
        <t-tag :theme="wsStatus === 'connected' ? 'success' : 'warning'">Node WS: {{ wsStatusText }}</t-tag>
      </div>
      <t-table
        row-key="rowKey"
        :data="records"
        :columns="columns"
        :loading="loading"
        :pagination="pagination"
        table-layout="fixed"
        :hover="true"
        @page-change="onPageChange"
      />
    </t-card>
  </div>
</template>

<script>
import axios from '../axios';
import { Button, MessagePlugin, Tag } from 'tdesign-vue-next';
import { computed, h, onBeforeUnmount, onMounted, ref } from 'vue';

const NODE_ADMIN_WS_URL = process.env.VUE_APP_NODE_ADMIN_WS_URL || 'wss://aka-cloud.china.gameplanet.vip/ws/admin/sessions';
const NODE_ADMIN_TOKEN = process.env.VUE_APP_NODE_ADMIN_TOKEN || '';
const WS_OPEN = 1;

export default {
  name: 'DenuvoTicketCandidates',
  setup() {
    const token = localStorage.getItem('user-token') || '';
    const loading = ref(false);
    const records = ref([]);
    const total = ref(0);
    const search = ref('');
    const currentPage = ref(1);
    const pageSize = ref(20);
    const onlineSteamIds = ref(new Set());
    const wsStatus = ref('closed');
    let socket = null;
    let pingTimer = null;
    let reconnectTimer = null;

    const wsStatusText = computed(() => ({ connected: '已连接', connecting: '连接中', error: '异常', closed: '未连接' }[wsStatus.value] || wsStatus.value));
    const pagination = computed(() => ({ current: currentPage.value, pageSize: pageSize.value, total: total.value, showJumper: true, showPageSize: true }));

    const rowKey = (row) => `${row.steamId64}-${row.appid}`;
    const safe = (v) => (v === undefined || v === null || v === '' ? '-' : String(v));
    const isOnline = (steamId64) => onlineSteamIds.value.has(String(steamId64 || ''));

    const sendJson = (payload) => {
      if (!socket || socket.readyState !== WS_OPEN) return false;
      socket.send(JSON.stringify(payload));
      return true;
    };

    const closeSocket = () => {
      if (pingTimer) window.clearInterval(pingTimer);
      if (reconnectTimer) window.clearTimeout(reconnectTimer);
      pingTimer = null;
      reconnectTimer = null;
      if (socket) {
        socket.onopen = null; socket.onmessage = null; socket.onerror = null; socket.onclose = null;
        try { socket.close(); } catch (_) {}
      }
      socket = null;
    };

    const scheduleReconnect = () => {
      if (reconnectTimer) return;
      reconnectTimer = window.setTimeout(() => { reconnectTimer = null; connectSocket(); }, 5000);
    };

    const connectSocket = () => {
      if (!NODE_ADMIN_TOKEN) { wsStatus.value = 'closed'; return; }
      closeSocket();
      wsStatus.value = 'connecting';
      socket = new WebSocket(NODE_ADMIN_WS_URL);
      socket.onopen = () => {
        wsStatus.value = 'connected';
        sendJson({ type: 'admin_hello', token: NODE_ADMIN_TOKEN });
        pingTimer = window.setInterval(() => sendJson({ type: 'ping' }), 25000);
      };
      socket.onmessage = (event) => {
        let msg;
        try { msg = JSON.parse(event.data); } catch (_) { return; }
        if (msg.type === 'session_snapshot') {
          onlineSteamIds.value = new Set((msg.sessions || []).map((s) => String(s.steamId64 || '')).filter(Boolean));
        }
        if (msg.type === 'command_ack') MessagePlugin.success('提取命令已下发');
        if (msg.type === 'error') MessagePlugin.error(msg.message || 'Node WS 错误');
      };
      socket.onerror = () => { wsStatus.value = 'error'; };
      socket.onclose = () => { wsStatus.value = wsStatus.value === 'error' ? 'error' : 'closed'; socket = null; scheduleReconnect(); };
    };

    const loadData = async (page = currentPage.value) => {
      loading.value = true;
      try {
        const { data } = await axios.post('/index.php?action=getDenuvoTicketCandidates', {
          token,
          search: search.value,
          page,
          size: pageSize.value,
        });
        if (data.code !== 200) throw new Error(data.message || '查询失败');
        records.value = (data.records || []).map((row) => ({ ...row, rowKey: rowKey(row) }));
        total.value = Number(data.total || 0);
        currentPage.value = Number(data.page || page);
      } catch (error) {
        MessagePlugin.error(error.response?.data?.message || error.message || '查询失败');
      } finally {
        loading.value = false;
      }
    };

    const onPageChange = ({ current, pageSize: nextSize }) => {
      currentPage.value = current;
      pageSize.value = nextSize;
      loadData(current);
    };

    const extractTicket = (row) => {
      if (!isOnline(row.steamId64)) return MessagePlugin.warning('该 SteamID 当前不在线，无法下发提取命令');
      const ok = sendJson({
        type: 'command',
        action: 'extract_tickets',
        steamId64: String(row.steamId64),
        appid: Number(row.appid),
        username: String(row.username || ''),
        reason: 'admin_denuvo_candidate_extract',
      });
      if (!ok) MessagePlugin.error('Node WebSocket 未连接，命令未发送');
    };

    const columns = computed(() => [
      { colKey: 'steamId64', title: 'SteamID', width: 180, fixed: 'left', cell: (render, { row }) => h('strong', safe(row.steamId64)) },
      { colKey: 'username', title: '游戏星球账号', width: 160, cell: (render, { row }) => safe(row.username) },
      { colKey: 'online', title: '在线状态', width: 110, cell: (render, { row }) => h(Tag, { theme: isOnline(row.steamId64) ? 'success' : 'default', variant: 'light' }, () => isOnline(row.steamId64) ? '在线' : '离线') },
      { colKey: 'appid', title: '命中游戏AppID', width: 140, cell: (render, { row }) => safe(row.appid) },
      { colKey: 'gameTitle', title: '命中游戏名称', minWidth: 240, ellipsis: true, cell: (render, { row }) => safe(row.gameTitle) },
      { colKey: 'libraryCount', title: '库内游戏数量', width: 130, cell: (render, { row }) => safe(row.libraryCount) },
      { colKey: 'ticketState', title: '已上传票据', width: 130, cell: (render, { row }) => `${Number(row.eticketUploaded) ? 'E' : '-'} / ${Number(row.appTicketUploaded) ? 'A' : '-'}` },
      { colKey: 'operation', title: '操作', width: 140, fixed: 'right', cell: (render, { row }) => h(Button, { theme: 'primary', size: 'small', disabled: !isOnline(row.steamId64), onClick: () => extractTicket(row) }, () => '发起提取命令') },
    ]);

    onMounted(() => { connectSocket(); loadData(1); });
    onBeforeUnmount(closeSocket);

    return { columns, currentPage, loadData, loading, onPageChange, pagination, records, search, wsStatus, wsStatusText };
  },
};
</script>

<style scoped>
.candidate-page { display: grid; gap: 16px; }
.panel { border: 1px solid #e7edf5; border-radius: 8px; }
.toolbar { display: flex; align-items: center; gap: 12px; margin-bottom: 16px; flex-wrap: wrap; }
.search { max-width: 460px; min-width: 280px; }
</style>
